import i18n from 'i18next';
import * as React from 'react';

import { MenuItem } from '@mui/material';
import {
  GridColDef,
  GridApiPro,
  useGridApiContext,
  GridValidRowModel,
  GridToolbarContainer,
  GridCsvExportOptions,
  GridToolbarExportContainer,
} from '@mui/x-data-grid-pro';

import useLocales from '../../hooks/useLocales';
import { getStatusString } from '../documentUtils';
import convertDateToLocaleString from '../shared/convertDateToLocaleString';

// ----------------------------------------------------------------------

// serialize an array of arrays to CSV
function serializeToCsv(rows: string[][], delimiter = ',') {
  return rows
    .map((row) =>
      row
        .map((cell) => {
          // escape quotes
          const v = cell?.toString().replace(/"/g, '""');
          return `"${v}"`;
        })
        .join(delimiter)
    )
    .join('\r\n');
}

// custom export function
function exportWithTransform(
  apiRef: React.MutableRefObject<GridApiPro>,
  locale: string,
  options: GridCsvExportOptions = {}
) {
  const header = apiRef.current
    .getVisibleColumns()
    .filter(
      (col: GridColDef<GridValidRowModel>) => col.field !== '__check__' && col.field !== 'actions'
    )
    .map((col: GridColDef<GridValidRowModel>) =>
      col.headerName ? col.headerName.toUpperCase() : col.field.toUpperCase()
    );

  // build body rows
  const body = Array.from(apiRef.current.getRowModels().values()).map((rowModel: any) =>
    apiRef.current
      .getVisibleColumns()
      .filter(
        (col: GridColDef<GridValidRowModel>) => col.field !== '__check__' && col.field !== 'actions'
      )
      .map((col: GridColDef<GridValidRowModel>) => {
        const row = apiRef.current.getRow(rowModel.id);
        let value = rowModel[col.field];

        if (col.type === 'date' || col.type === 'dateTime') {
          value = convertDateToLocaleString(
            col.field === 'created' || col.field === 'modified' ? 'dateTime' : col.type,
            value,
            locale
          );
        }

        if (col.field === 'ownerFullName') {
          value = rowModel.ownerFullName || rowModel.ownerEmail;
        }

        if (col.field === 'status') {
          value = i18n.t(getStatusString(value));
        }

        if (col.field === 'signatures') {
          value = `${row.alreadySigned.toString()} -> ${row.totalSigners.toString()}`;
        }

        if (col.field === 'tags') {
          value = value.map((tag: any) => tag.name).join(', ');
        }

        if (value === undefined || value === null) {
          value = '';
        }

        return value;
      })
  );

  // stitch it all together
  let csvContent = serializeToCsv([header, ...body], options.delimiter);
  if (options.utf8WithBom) {
    csvContent = `\uFEFF${csvContent}`;
  }

  // trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', options.fileName || 'export.csv');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// a custom toolbar that uses it
function GridToolbarCustomExport() {
  const apiRef = useGridApiContext();
  const { t, currentLang } = useLocales();

  const locale = currentLang.regionSpecificLocale;

  return (
    <GridToolbarContainer>
      <GridToolbarExportContainer>
        <MenuItem
          onClick={() =>
            exportWithTransform(apiRef, locale, {
              fileName:
                `data-export-${document.title.toLowerCase()}-${new Date().toLocaleDateString(locale)}`
                  .replaceAll(' ', '-')
                  .replaceAll('.', '-'),
              delimiter: ';',
              utf8WithBom: true,
            })
          }
        >
          {t('documents.toolbarExportCSV')}
        </MenuItem>
      </GridToolbarExportContainer>
    </GridToolbarContainer>
  );
}

export default GridToolbarCustomExport;
